import { NoteEditorSourceBarChip } from "@/components/note/editor/source-bar/note-editor-source-bar-chip";
import { INoteObservable } from "@/store/note/types";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useRef } from "react";
import { generateRecentDateString } from "@/domains/date/date";
import { getSharedWithLabel } from "@/domains/sharing/getSharedWithLabel";
import { withLineBreaks } from "@/domains/html/withLineBreaks";
import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSTooltipTrigger,
  MDSFadeHorizontalScroll,
} from "@/mds";
import { MDSPanelWidths } from "@/mds/foundations";

interface NoteEditorBottomBarProps {
  note: INoteObservable;
  tooltipLabel: React.ReactNode;
  containerWidth?: number;
  onOpenVersionHistory?: () => void;
  onOpenShareSheet?: () => void;
}

export const NoteEditorBottomBar = observer<NoteEditorBottomBarProps>(
  ({ note, tooltipLabel, containerWidth, onOpenVersionHistory, onOpenShareSheet }) => {
    const sources = note.sourceList?.allSupportedSources ?? [];

    const playingSourceIdRef = useRef<string | null>(null);

    const collapsedChips =
      typeof containerWidth === "number" && containerWidth <= MDSPanelWidths.narrow;

    const hasSuggestion = !!note?.suggestionData;

    const sharedWithLabel = getSharedWithLabel(note.sharedWithLabels);

    const statusText = note.isShared ? "Shared" : "Private";

    // Both halves of the footer line share this fixed-height, centered box so
    // they sit on one vertical center.
    const fixedLineTriggerStyle = {
      display: "inline-flex",
      alignItems: "center",
      height: LINE_HEIGHT,
    } as const;

    // Icon + status word form a single hover target for the shared-with tooltip.
    const statusGroup = (
      <StatusGroup>
        <MDSIcon
          kind={note.isShared ? MDSIconKind.Shared : MDSIconKind.Lock}
          size={10}
          color={MDSIconColor.Secondary}
        />
        <MDSText variant={MDSTextVariant.TextXs}>{statusText}</MDSText>
      </StatusGroup>
    );

    // When sharing can be opened, the status (Shared/Private) becomes a real,
    // focusable control that opens the share sheet — mirroring the metadata
    // half, which opens version history.
    const statusLabel = onOpenShareSheet ? (
      <StatusButton type="button" onClick={onOpenShareSheet}>
        {statusGroup}
      </StatusButton>
    ) : (
      statusGroup
    );

    const metadataText = (
      <MDSText variant={MDSTextVariant.TextXs} overflow={MDSTextOverflow.Ellipsis}>
        {note.isMemIngestionEcho ? (
          <>
            Created{" "}
            {generateRecentDateString(note.locallyCreatedAt, {
              relativeDateShouldBeLowercase: true,
            })}{" "}
            by {note.attributedCreator?.profileDisplayName}
          </>
        ) : (
          <>
            Edited{" "}
            {generateRecentDateString(note.modifiedAt, {
              relativeDateShouldBeLowercase: true,
            })}{" "}
            by {note.lastModifier?.profileDisplayName}
          </>
        )}
      </MDSText>
    );

    return (
      <Container $hasSuggestion={hasSuggestion}>
        <ScrollableArea>
          <MDSFadeHorizontalScroll>
            <InnerContainer>
              {sharedWithLabel ? (
                <MDSTooltip
                  config={{
                    label: withLineBreaks(sharedWithLabel),
                    placement: MDSTooltipPlacement.Top,
                  }}
                >
                  <MDSTooltipTrigger style={fixedLineTriggerStyle}>{statusLabel}</MDSTooltipTrigger>
                </MDSTooltip>
              ) : (
                statusLabel
              )}
              <Bullet aria-hidden="true" />
              <MDSTooltip
                config={{
                  label: tooltipLabel,
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <MDSTooltipTrigger style={fixedLineTriggerStyle}>
                  {onOpenVersionHistory ? (
                    <MetadataButton type="button" onClick={onOpenVersionHistory}>
                      {metadataText}
                    </MetadataButton>
                  ) : (
                    metadataText
                  )}
                </MDSTooltipTrigger>
              </MDSTooltip>
              <Spacer />
              {sources.length > 0 && (
                <MDSIcon kind={MDSIconKind.Paperclip} size={12} color={MDSIconColor.Secondary} />
              )}
              {sources.map(source => (
                <NoteEditorSourceBarChip
                  isCollapsed={collapsedChips}
                  key={source.id}
                  source={source}
                  note={note}
                  playingSourceIdRef={playingSourceIdRef}
                />
              ))}
            </InnerContainer>
          </MDSFadeHorizontalScroll>
        </ScrollableArea>
      </Container>
    );
  }
);
NoteEditorBottomBar.displayName = "NoteEditorBottomBar";

const Container = styled.div<{ $hasSuggestion: boolean }>`
  display: flex;
  flex-shrink: 0;
  box-sizing: border-box;
  justify-content: flex-start;
  align-items: center;
  height: 40px;
  padding: ${({ theme }) => `0 ${theme.spacing["3"]} 0 ${theme.spacing["7"]}`};
  background-color: ${({ theme, $hasSuggestion }) =>
    $hasSuggestion ? theme.colors.background.diff : theme.colors.surface.pane};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-top: 1px solid ${({ theme }) => theme.colors.surface.chrome};
`;

const ScrollableArea = styled.div`
  flex-grow: 1;
  /* Hide the native scrollbar, MDSFadeHorizontalScroll provides its own */
  overflow: hidden;
`;

const Spacer = styled.div`
  flex: 1;
`;

// Fixed line height so the icon, status word, and metadata all center together.
const LINE_HEIGHT = "16px";

// Icon + status word as a single hover target, locked to the shared line height.
const StatusGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  flex-shrink: 0;
  height: ${LINE_HEIGHT};
  line-height: ${LINE_HEIGHT};
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  min-width: 100%;
`;

// CSS-dot separator (the "•" glyph sits high due to font metrics).
const Bullet = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${LINE_HEIGHT};
  flex-shrink: 0;

  &::before {
    content: "";
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.text.secondary};
    /* Align to the text's optical middle. */
    position: relative;
    top: 1px;
  }
`;

// Plain-text-looking button so the status (Shared/Private) stays visually
// identical to the non-clickable variant while still being a real, focusable
// control that opens the share sheet.
const StatusButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  height: ${LINE_HEIGHT};
  line-height: ${LINE_HEIGHT};
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }
`;

// Plain-text-looking button so the metadata line stays visually identical to
// the non-clickable variant while still being a real, focusable control.
const MetadataButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  height: ${LINE_HEIGHT};
  line-height: ${LINE_HEIGHT};
  min-width: 0;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }
`;
